import axios from 'axios';

import { useMutation, useQuery } from 'react-query';
import { AutomaticNetworkType, AutomaticSchema } from './_models';

const AUTOMATIC_TRC20 = `/api/admin/manager/tron/transactions`;
const AUTOMATIC_BEP20 = `/api/admin/manager/bnb/transactions`;
const CRYPTOMUS_TRANSACTION = `/api/admin/manager/cryptomus/transactions`;

const AMOUNT_LIMITATION = `/api/admin/manager/settings/amount-limitation`;

const endpointMap: Record<AutomaticNetworkType, string> = {
	trc20: AUTOMATIC_TRC20,
	bep20: AUTOMATIC_BEP20,
	cryptomus: CRYPTOMUS_TRANSACTION,
};

export const useGetAutomatic = (
	network: AutomaticNetworkType,
	currentItemsValue: number,
	itemsValue: number
) => {
	return useQuery<AutomaticSchema[], any>(
		['get-automatic', network, currentItemsValue, itemsValue],
		async () => {
      const endpoint = endpointMap[network];
      if (!endpoint) {
        throw new Error(`Unknown network: ${network}`);
      }
			const { data } = await axios.get(
				`${endpoint}?limit=${itemsValue}&offset=${currentItemsValue}&verify=false`
			);
			return data.data;
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			refetchInterval: 15000,
			retry: 0,
		}
	);
};

export const useGetAutomaticBEP20 = (
	currentItemsValue: number,
	itemsValue: number
) => {
	return useQuery<AutomaticSchema[], any>(
		['get-automatic-bep20', currentItemsValue, itemsValue],
		async () => {
			const { data } = await axios.get(
				`${AUTOMATIC_BEP20}?limit=${itemsValue}&offset=${currentItemsValue}&verify=false`
			);
			return data.data;
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			refetchInterval: 15000,
		}
	);
};

export const useAmountLimitation = () => {
	return useMutation<any, any, any>(
		'get-automatic',
		async (reqData: { amount: number; verification_code: string }) => {
			const { data } = await axios.patch(`${AMOUNT_LIMITATION}`, reqData);
			return data.data;
		}
	);
};

const SETTINGS = `/api/admin/manager/settings`;

export const useFeeLimitation = () =>
	useMutation<any, any, { fee_limit: number }>(
		'fee-limitation',
		async reqData => {
			const { data } = await axios.patch(`${SETTINGS}/fee-limitation`, reqData);
			return data;
		}
	);

export const useUpdateVerificationCode = () =>
	useMutation<any, any, { old_code: string; new_code: string }>(
		'update-verification-code',
		async reqData => {
			const { data } = await axios.patch(`${SETTINGS}/verification-code`, reqData);
			return data;
		}
	);

const PRIVATE_KEY_MAP: Partial<Record<AutomaticNetworkType, string>> = {
	trc20: `/api/admin/manager/wallets/tron/private-key`,
	bep20: `/api/admin/manager/wallets/bnb/private-key`,
};

export const useSavePrivateKey = (network: AutomaticNetworkType) => {
	return useMutation<any, any, { private_key: string; verification_code: string }>(
		'save-private-key',
		async reqData => {
			const { data } = await axios.post(PRIVATE_KEY_MAP[network]!, reqData);
			return data;
		}
	);
};
